import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { QrCode, Printer, Copy, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { toast } from "../ui/sonner";
import { openAuthedPdf } from "../../lib/pdf";

/* 项目二维码：仓库出库 / 退料时扫此码锁定目标项目；可打印 PDF 卡（大码 + 3 张小标签）。
 * 入口下沉到编辑弹窗后，使用 Radix 子弹窗挂到 Portal，避免原 fixed 遮罩被父弹窗裁切，
 * 并保证 Esc、焦点恢复及未保存表单的状态正常；api / project / zh 沿用原接口与语言。 */
export const ProjectQrButton = ({ api, project, zh }) => {
  const T = (a, b) => (zh ? a : b);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const code = `ALG:P:${project.id}`;
  const url = `${window.location.origin}/warehouses/scan?code=${encodeURIComponent(code)}`;
  const printCard = async () => {
    setBusy(true);
    try { await openAuthedPdf(api, `/wms/labels/project.pdf?pid=${project.id}`, `project_qr_${project.id}.pdf`); }
    catch (e) { toast.error(e?.response?.data?.detail || T("生成失败", "Failed")); }
    setBusy(false);
  };
  const copy = async () => { try { await navigator.clipboard.writeText(code); toast.success(T("已复制编码", "Copied")); } catch { toast.error(T("复制失败", "Copy failed")); } };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button type="button" data-testid="hub-project-qr-btn" size="sm" variant="outline" className="h-9 text-xs text-slate-700"><QrCode className="h-3.5 w-3.5 mr-1" aria-hidden="true" />{T("项目二维码", "Project QR")}</Button></DialogTrigger>
      <DialogContent className="max-w-sm" data-testid="hub-project-qr-modal" closeLabel={T("关闭", "Close")}>
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-1.5"><QrCode className="h-4 w-4 text-brand-500" aria-hidden="true" />{T("项目二维码", "Project QR")}</DialogTitle>
          <DialogDescription className="sr-only">{T("扫描或打印项目二维码", "Scan or print the project QR code")}</DialogDescription>
        </DialogHeader>
        <div>
          <div className="flex justify-center rounded-xl border border-slate-200 p-4 bg-white" data-testid="hub-project-qr-svg"><QRCodeSVG value={url} size={220} level="M" includeMargin={false} /></div>
          <p className="text-sm font-semibold text-slate-800 text-center mt-3">{project.address}</p>
          <p className="text-[11px] text-slate-400 text-center">{project.id} · {project.clientName || "-"} · {T("经理", "PM")} {project.manager || "-"}</p>
          <p className="text-[10px] text-slate-400 text-center mt-1 font-mono" data-testid="hub-project-qr-code">{code}</p>
          <p className="text-[10px] text-slate-300 text-center font-mono break-all" data-testid="hub-project-qr-url">{url}</p>
          <p className="text-[11px] text-slate-500 text-center mt-2">{T("手机相机直接扫 → 打开扫码工作台并自动识别；仓库出库 / 退料时先扫此码锁定目标项目。", "Scan with a phone camera to open the scan station; sets this project as the issue / return target.")}</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button data-testid="hub-project-qr-print" size="sm" disabled={busy} onClick={printCard} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Printer className="h-3.5 w-3.5 mr-1" />}{T("打印二维码卡 PDF", "Print QR card")}</Button>
            <Button data-testid="hub-project-qr-copy" size="sm" variant="outline" onClick={copy}><Copy className="h-3.5 w-3.5 mr-1" />{T("复制编码", "Copy")}</Button>
            <DialogClose asChild><Button type="button" data-testid="hub-project-qr-close" size="sm" variant="outline">{T("关闭", "Close")}</Button></DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
